<script setup lang="ts">
import { formatInstantDate, formatInstantTimeUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { notificationTarget } from '../internal/routing.ts'
import type { Notification } from '../internal/types.ts'

const props = defineProps<{ notification: Notification; activeLedgerId: string | undefined }>()
const emit = defineEmits<{ markRead: [notificationId: string] }>()

const { t, locale } = useI18n()

const isUnread = computed(() => props.notification.readAt === undefined)
const target = computed(() => notificationTarget(props.notification, props.activeLedgerId))
const timestamp = computed(
  () =>
    `${formatInstantDate(props.notification.createdAt, locale.value as 'en' | 'hu')} ${formatInstantTimeUtc(props.notification.createdAt)}`,
)
</script>

<template>
  <li
    class="flex flex-col gap-[var(--space-tight)] border-b border-[color:var(--color-border)] px-[var(--space-cell)] py-[var(--space-inline)] last:border-b-0"
  >
    <div class="flex items-start justify-between gap-[var(--space-cell)]">
      <div class="flex items-start gap-[var(--space-tight)]">
        <span
          v-if="isUnread"
          aria-hidden="true"
          class="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-[color:var(--color-action)]"
        />
        <div>
          <component
            :is="target ? 'RouterLink' : 'p'"
            :to="target"
            :class="[
              'text-[length:var(--text-step-3)]',
              isUnread
                ? 'font-semibold text-[color:var(--color-text)]'
                : 'text-[color:var(--color-text-muted)]',
              target ? 'hover:underline' : '',
            ]"
          >
            {{ notification.title }}
          </component>
          <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
            {{ notification.body }}
          </p>
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{ timestamp }}</p>
        </div>
      </div>
      <button
        v-if="isUnread"
        type="button"
        class="min-h-[var(--density-target-min)] shrink-0 rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)]"
        @click="emit('markRead', notification.id)"
      >
        {{ t('notifications.markRead') }}
      </button>
    </div>
  </li>
</template>
